import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { WidgetViewModel } from '../../viewModel/viewModel';
import { getResourceQueryParam } from '../../utils/queryParams/queryParams';
import { getLocationId } from '../../utils/getLocationId/getLocationId';
import {
  DialogType,
  ServicePreferencesDialogViewModel,
} from '../../viewModel/dialogViewModel/dialogViewModel';
import {
  Basket,
  PreselectedValues,
} from '@wix/bookings-services-preferences-modal/types';
import { EnrichedService } from '../../types/types';
import { ServiceListContext } from '../../context/createServiceListContext';
import { isServiceDynamicPricing } from '../../utils/services/services';
import { DO_NOT_CARE_STAFF } from '../../consts';

export type openServicesPreferencesModalAction = ({
  service,
}: {
  service: EnrichedService;
}) => Promise<void>;

export const createOpenServicesPreferencesModalAction = ({
  flowAPI,
  widgetViewModel,
  setProps,
  serviceListContext,
}: {
  flowAPI: ControllerFlowAPI;
  widgetViewModel: WidgetViewModel;
  serviceListContext: ServiceListContext;
  setProps: Function;
}): openServicesPreferencesModalAction => {
  const { businessInfo } = serviceListContext;

  return async ({ service }) => {
    const { createModalData } = await import(
      '@wix/bookings-services-preferences-modal/utils'
    );
    const { enrichModalData } = await import(
      '@wix/bookings-services-preferences-modal/controllerActions'
    );

    const queryParam = getResourceQueryParam(
      flowAPI.controllerConfig.wixCodeApi,
    );
    const locationId = getLocationId(flowAPI, widgetViewModel);
    const preselectedValues: PreselectedValues = {
      locationId,
      ...(queryParam.resource
        ? {
            resources: {
              staff: [queryParam.resource],
            },
          }
        : {}),
    };

    const basket = flowAPI.experiments.enabled('specs.bookings.openMSAInBasket')
      ? getBasket(service)
      : undefined;

    const modalData = createModalData({
      regionalSettingsLocale: businessInfo.regionalSettings,
      service,
      preselectedValues,
      basket,
    });

    const servicePreferencesDialogViewModel: ServicePreferencesDialogViewModel =
      {
        isOpen: true,
        type: DialogType.ServicePreferences,
        data: modalData,
      };

    widgetViewModel.dialogViewModel = servicePreferencesDialogViewModel;

    setProps({
      widgetViewModel: {
        ...widgetViewModel,
      },
    });

    enrichModalData({
      service,
      httpClient: flowAPI.httpClient,
      getServicesPreferencesModalData: () =>
        servicePreferencesDialogViewModel.data,
      setServicesPreferencesModalData: (data) => {
        servicePreferencesDialogViewModel.data = data;
        setProps({ widgetViewModel: { ...widgetViewModel } });
      },
      experiments: flowAPI.experiments,
      basket,
    });
  };
};

const getBasket = (service: EnrichedService): Basket | undefined => {
  const { locations, staffMemberIds } = service;
  const location = locations?.[0];

  if (locations?.length! > 1 || isServiceDynamicPricing(service)) {
    return;
  }

  const selectedStaff =
    staffMemberIds!.length > 1 ? DO_NOT_CARE_STAFF : staffMemberIds?.[0]!;

  return {
    location: {
      type: location?.type!,
      value:
        location?.business?.id ||
        location?.custom?.address?.formattedAddress ||
        '',
    },
    selectedServicesPreferences: [
      {
        service,
        resources: {
          staff: [selectedStaff],
        },
      },
    ],
  };
};
